import { createSelector } from 'reselect';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../../constants/bucket';
import { DISABLE_HANDWRITTEN_NOTE_STEP } from '../../constants/campaigns';
import { DEFAULT_CREATE_SEND_SHIPMENT_INFO_STATE } from '../../constants/createSendShipmentInfo';
import { resolveCreateSendSteps } from '../../constants/createSendSteps';
import { IOrgAddress } from '../../types/addressBook';
import { isMarketplaceBucketItem, TCreateSendBucketItem } from '../../types/bucket';
import { ICampaignItem } from '../../types/campaigns';
import { ContentsTabGroupEnum, ContentsTabIdEnum, IContentsGate } from '../../types/createSendContents';
import { IEffectiveCreateSendCustomMessage } from '../../types/createSendCustomMessage';
import { IEffectiveCreateSendEmail } from '../../types/createSendEmail';
import {
  CreateSendContentTypeEnum,
  CreateSendMethodEnum,
  ICreateSendStepContext,
  TCreateSendContentType,
  TCreateSendStep,
} from '../../types/createSendFlow';
import { IEffectiveCreateSendClaimPage, IEffectiveCreateSendLinkSettings } from '../../types/createSendLinkSettings';
import {
  IEffectiveCreateSendRecipients,
  ShippingModeEnum,
  TEffectiveShippingMode,
} from '../../types/createSendRecipients';
import { IEffectiveCreateSendInternalRecords, IEffectiveCreateSendSettings } from '../../types/createSendSettings';
import { IEffectiveCreateSendShipmentInfo } from '../../types/createSendShipmentInfo';
import { isCampaignEligibleToOrder, isDigitalBox } from '../../utils/campaigns';
import { getLocationContentsIssue, TLocationContentItem } from '../../utils/createSendLocation';
import { isItemOutOfStock } from '../../utils/inventories';
import { isMarketplaceItemOutOfStock } from '../../utils/marketplaceStock';
import { selectCsClaimPageExperienceId } from '../createSend/selectors/claimPage';
import {
  selectCsBoxId,
  selectCsContentsActiveTab,
  selectCsHasGiftBudget,
  selectCsHasMskuContent,
  selectCsIsContentDigital,
  selectCsIsGiftBudgetPending,
  selectCsItems,
  selectCsKitIds,
  selectCsKits,
  selectCsMarketplacePygPicks,
} from '../createSend/selectors/contents';
import { selectCsUnsetMskuState } from '../createSend/selectors/contentsAttempt';
import { selectCsCustomMessageState, selectCsIsCustomMessageStepValid } from '../createSend/selectors/customMessage';
import {
  selectCsEmailInitialized,
  selectCsEmailIsOn,
  selectCsEmailState,
  selectCsEmailTokenGate,
  selectCsIsEmailStepValid,
  selectCsReminderInterval,
} from '../createSend/selectors/email';
import {
  selectCsAllowedDomains,
  selectCsIs2FaEnabled,
  selectCsLinkExpirationDate,
  selectCsLinkOnHoldUntil,
  selectCsLinkPassword,
  selectCsLinkTitle,
  selectCsRedemptionLimit,
} from '../createSend/selectors/linkSettings';
import { selectIsCsLocationSendEmpty } from '../createSend/selectors/locationUnset';
import { selectCreateSendMethod } from '../createSend/selectors/method';
import { selectIsCsOrgSettingsResolved } from '../createSend/selectors/orgSettings';
import {
  selectCompleteRecipients,
  selectCsAddressMode,
  selectCsClaimAddressIds,
  selectCsIsGroupShipmentApplicable,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsRecipientsMode,
  selectEffectiveShipping,
  selectRecipientsGate,
} from '../createSend/selectors/recipients';
import {
  selectCsBusinessReasonId,
  selectCsClaimExpiration,
  selectCsDisableRecipientShippedEmail,
  selectCsDisableSenderEmails,
  selectCsInternalNotes,
  selectCsIsOfficeDropshipEnabled,
  selectCsIsSobActive,
  selectCsIsSobUserEmailIntegrationDisabled,
  selectCsOnHoldEnabled,
  selectCsOnHoldUntil,
  selectCsSobEmail,
  selectCsSobEnabled,
  selectIsCsReleaseDateMissing,
} from '../createSend/selectors/sendSettings';
import { selectCsShipmentInfoForm, selectShipmentInfoGate } from '../createSend/selectors/shipmentInfo';
import {
  selectIsLinkOnHoldApplicable,
  selectIsLinkPayloadDigital,
  selectLinkSettingsGate,
} from './createSendLinkSettings';
import { selectIsEmailAuthenticated } from './integrations';
import { selectIsCurrentOrganizationSupportedAnyEmail } from './organizations';
import {
  selectEmailDelayedShippingTemplates,
  selectEmailDigitalGiftTemplates,
  selectEmailTemplatesNeverLoaded,
} from './templates';

export { selectEffectiveShipping, selectIsMultiKit, selectKitsCount } from '../createSend/selectors/recipients';

export { selectCreateSendMethod };

export const selectCreateSendRecipientsMode = selectCsRecipientsMode;

export const selectDirectSendKits = createSelector(selectCsKits, (kits) => kits ?? []);

export const selectDirectSendItems = createSelector(selectCsItems, (items) => items ?? []);

/**
 * PYG means the recipient chooses, so anything that creates a choice is a PYG: 2+ options (kits
 * and picks count alike), or a single option / custom kit beside a recipient budget. A budget
 * alone is not — with nothing selected there is no option to choose against.
 */
export const selectCreateSendContentType = createSelector(
  selectCreateSendMethod,
  selectCsBoxId,
  selectCsKitIds,
  selectCsMarketplacePygPicks,
  selectCsHasGiftBudget,
  selectCsItems,
  (method, boxId, pygCampaignIds, marketplacePygPicks, hasGiftBudget, items): TCreateSendContentType | null => {
    if (method === CreateSendMethodEnum.Location) {
      return null;
    }

    if (boxId === CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID) {
      // The assembled kit is one option, so a budget beside it makes a choice.
      return hasGiftBudget && (items?.length ?? 0) > 0
        ? CreateSendContentTypeEnum.Pyg
        : CreateSendContentTypeEnum.Custom;
    }

    const optionCount = (pygCampaignIds?.length ?? 0) + marketplacePygPicks.length;

    if (optionCount >= 2) {
      return CreateSendContentTypeEnum.Pyg;
    }

    if (optionCount === 1) {
      return hasGiftBudget ? CreateSendContentTypeEnum.Pyg : CreateSendContentTypeEnum.Single;
    }

    if (boxId) {
      return CreateSendContentTypeEnum.Single;
    }

    if (items?.length) {
      return CreateSendContentTypeEnum.Custom;
    }

    return null;
  },
);

export const selectIsCreateSendContentDigital = selectCsIsContentDigital;

export const selectHasMskuContent = selectCsHasMskuContent;

export const selectIsShippingAddressApplicable = createSelector(
  selectIsCreateSendContentDigital,
  (isContentDigital): boolean => !isContentDigital,
);

export const selectIsCreateSendNotecardDisabled = createSelector(selectCsKits, (kits): boolean => {
  const physicalKits = (kits ?? []).filter((kit) => !isDigitalBox(kit));

  return physicalKits.length > 0 && physicalKits.every((kit) => Boolean(kit[DISABLE_HANDWRITTEN_NOTE_STEP]));
});

export const selectCreateSendContentsValid = createSelector(
  selectCsKitIds,
  selectCsItems,
  (campaignIds, items): boolean => Boolean(campaignIds?.length || items?.length),
);

/**
 * Marketplace picks carry none of the inventory eligibility fields (`type`,
 * `preorder_allowed`, `sku_options`), so ordering reduces to "is there stock".
 * Splitting them out keeps `isCampaignEligibleToOrder` reading real inventory shapes.
 */
const isBucketItemOrderable = (item: TCreateSendBucketItem): boolean =>
  isMarketplaceBucketItem(item)
    ? // `>= quantity`, not `> 0`: the IE path applies the same rule via
      // `isCampaignEligibleToOrder`, so asking for 50 of a count-1 item must fail here too.
      !isMarketplaceItemOutOfStock(item) && item.count >= item.quantity
    : Boolean(isCampaignEligibleToOrder([item as ICampaignItem]));

/** Marketplace picks have no preorder/MSKU nuance — stock alone decides. */
const isBucketItemOutOfStock = (item: TCreateSendBucketItem): boolean =>
  isMarketplaceBucketItem(item) ? isMarketplaceItemOutOfStock(item) : isItemOutOfStock(item);

const selectContentsHasOutOfStock = createSelector(
  selectCsKits,
  selectCsItems,
  (kits, items): boolean =>
    (kits ?? []).some((kit) => !isCampaignEligibleToOrder(kit.items)) ||
    (items ?? []).some((item) => !isBucketItemOrderable(item)),
);

const selectContentsHasGenuineOutOfStock = createSelector(
  selectCsKits,
  selectCsItems,
  (kits, items): boolean =>
    (kits ?? []).some((kit) => (kit.items ?? []).some(isItemOutOfStock)) || (items ?? []).some(isBucketItemOutOfStock),
);

const selectContentsHasInsufficientItems = createSelector(selectCsItems, (items): boolean =>
  (items ?? []).some((item) => !isBucketItemOrderable(item) && !isBucketItemOutOfStock(item)),
);

const selectLocationContentsIssue = createSelector(selectCsKits, selectCsItems, (kits, items) =>
  getLocationContentsIssue(kits, items as TLocationContentItem[] | undefined),
);

export const selectContentsGate = createSelector(
  selectCreateSendMethod,
  selectCreateSendContentsValid,
  selectContentsHasOutOfStock,
  selectCsContentsActiveTab,
  selectContentsHasGenuineOutOfStock,
  selectContentsHasInsufficientItems,
  selectLocationContentsIssue,
  selectIsCsLocationSendEmpty,
  selectCsHasGiftBudget,
  selectCsIsGiftBudgetPending,
  (
    method,
    hasSelection,
    hasOutOfStock,
    activeTabMap,
    hasGenuineOutOfStock,
    hasInsufficientItems,
    locationIssue,
    isLocationSendEmpty,
    hasGiftBudget,
    isGiftBudgetPending,
  ): IContentsGate => {
    if (method === CreateSendMethodEnum.Location) {
      if (!hasSelection) {
        return { valid: false, reason: 'empty-location' };
      }
      if (locationIssue === 'digital') {
        return { valid: false, reason: 'digital-location' };
      }
      if (locationIssue === 'out-of-stock') {
        return { valid: false, reason: 'out-of-stock-location' };
      }
      if (locationIssue === 'insufficient-stock') {
        return { valid: false, reason: 'insufficient-stock-location' };
      }
      if (isLocationSendEmpty) {
        return { valid: false, reason: 'nothing-to-ship-location' };
      }
      return { valid: true, reason: null };
    }
    if (method !== CreateSendMethodEnum.Recipients && method !== CreateSendMethodEnum.Link) {
      return { valid: hasSelection, reason: null };
    }
    // Continuing would silently drop the budget, so the sender must finish or turn it off.
    if (isGiftBudgetPending) {
      return { valid: false, reason: 'gift-budget-incomplete' };
    }
    // "Gift budget only" is a valid send (PRD config #4): the budget IS the content.
    if (!hasSelection) {
      if (hasGiftBudget) {
        return { valid: true, reason: null };
      }

      const tab = activeTabMap[ContentsTabGroupEnum.RecipientsLink] ?? ContentsTabIdEnum.Curated;
      return { valid: false, reason: tab === ContentsTabIdEnum.Build ? 'empty-build' : 'empty-curated' };
    }
    if (hasOutOfStock) {
      const reason = !hasGenuineOutOfStock && hasInsufficientItems ? 'insufficient-stock' : 'out-of-stock';
      return { valid: false, reason };
    }
    return { valid: true, reason: null };
  },
);

export const selectIsCsEmailIntegrationPending = createSelector(
  selectIsCurrentOrganizationSupportedAnyEmail,
  selectCsIsSobActive,
  selectIsEmailAuthenticated,
  (isEmailSupported, isSobActive, isAuthenticated): boolean =>
    isEmailSupported !== false && !isSobActive && isAuthenticated === undefined,
);

const selectCsEmailStepTemplates = createSelector(
  selectIsCreateSendContentDigital,
  selectEmailDigitalGiftTemplates,
  selectEmailDelayedShippingTemplates,
  (isDigital, digitalTemplates, delayedTemplates) => (isDigital ? digitalTemplates : delayedTemplates) ?? [],
);

export const selectIsCsEmailSkippedBySob = createSelector(
  selectCsIsSobActive,
  selectCsIsSobUserEmailIntegrationDisabled,
  (isSobActive, isSobUserIntegrationDisabled): boolean => Boolean(isSobActive && isSobUserIntegrationDisabled),
);

export const selectIsCsEmailPreparing = createSelector(
  selectIsCsEmailSkippedBySob,
  selectIsCsEmailIntegrationPending,
  selectEmailTemplatesNeverLoaded,
  selectCsEmailInitialized,
  selectCsEmailStepTemplates,
  (isSkippedBySob, isIntegrationPending, templatesNeverLoaded, isInitialized, templates): boolean => {
    if (isSkippedBySob) {
      return false;
    }

    return isIntegrationPending || Boolean(templatesNeverLoaded) || (!isInitialized && templates.length > 0);
  },
);

export const selectCreateSendStepContext = createSelector(
  selectCreateSendMethod,
  selectCreateSendRecipientsMode,
  selectCreateSendContentType,
  selectContentsGate,
  selectRecipientsGate,
  selectShipmentInfoGate,
  selectLinkSettingsGate,
  selectCsAddressMode,
  selectEffectiveShipping,
  selectCsEmailIsOn,
  selectIsCreateSendContentDigital,
  selectIsLinkPayloadDigital,
  selectIsCreateSendNotecardDisabled,
  selectIsCsEmailPreparing,
  selectCsUnsetMskuState,
  selectCsIsEmailStepValid,
  selectCsEmailTokenGate,
  selectCsIsCustomMessageStepValid,
  selectIsCsReleaseDateMissing,
  (
    method,
    recipientsMode,
    contentType,
    contents,
    recipients,
    shipmentInfo,
    linkSettings,
    addressMode,
    effective,
    emailNotificationsOn,
    isContentDigital,
    isLinkPayloadDigital,
    isNotecardDisabled,
    isEmailPreparing,
    unsetMsku,
    isEmailStepValid,
    emailToken,
    isCustomMessageStepValid,
    isReleaseDateMissing,
  ): ICreateSendStepContext => ({
    method,
    recipientsMode,
    contentType,
    addressMode,
    isClaimBased: effective.isClaimBased,
    isContentDigital,
    isLinkPayloadDigital,
    isNotecardDisabled,
    emailNotificationsOn,
    isEmailPreparing,
    unsetMsku,
    emailToken,
    contents,
    recipients,
    shipmentInfo,
    linkSettings,
    validity: {
      email: isEmailStepValid,
      review: !isReleaseDateMissing,
      linkSettings: linkSettings.valid,
      claimPage: true,
      customMessage: isCustomMessageStepValid,
    },
  }),
);

export const selectCreateSendFlowSteps = createSelector(selectCreateSendStepContext, resolveCreateSendSteps);

export const selectIsCreateSendValidityResolved = selectIsCsOrgSettingsResolved;

export const selectIsCreateSendFlowComplete = createSelector(
  selectCreateSendFlowSteps,
  (steps): boolean => steps.length > 0 && steps.every((step) => step.valid),
);

export const selectCreateSendVisibleStepSlugs = createSelector(
  selectCreateSendFlowSteps,
  (steps): ReadonlySet<TCreateSendStep> => new Set(steps.map((step) => step.slug)),
);

export const selectIsCsReminderApplicable = createSelector(selectCreateSendVisibleStepSlugs, (visibleSteps): boolean =>
  visibleSteps.has('email'),
);

export const selectIsCsEmailIntegrationSatisfied = createSelector(
  selectIsCurrentOrganizationSupportedAnyEmail,
  selectCsIsSobActive,
  selectIsEmailAuthenticated,
  (isEmailSupported, isSobActive, isAuthenticated): boolean =>
    isEmailSupported !== false && !isSobActive ? isAuthenticated === true : true,
);

export const selectEffectiveCsEmail = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCsEmailState,
  selectIsCsEmailIntegrationSatisfied,
  (visibleSteps, email, isIntegrationSatisfied): IEffectiveCreateSendEmail => {
    const isOn = visibleSteps.has('email') && email.emailTemplateId !== null && isIntegrationSatisfied;

    return {
      isOn,
      emailTemplateId: isOn ? email.emailTemplateId : null,
      subject: isOn ? email.subject : '',
      body: isOn ? email.body : '',
    };
  },
);

export const selectEffectiveCsReminderInterval = createSelector(
  selectEffectiveCsEmail,
  selectCsReminderInterval,
  (email, interval): string => (email.isOn ? interval : ''),
);

export const selectEffectiveCsCustomMessage = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCsCustomMessageState,
  (visibleSteps, customMessage): IEffectiveCreateSendCustomMessage => {
    const enabled = visibleSteps.has('custom-message') && customMessage.enabled;

    return {
      enabled,
      templateId: enabled ? customMessage.templateId : null,
      message: enabled ? customMessage.message : '',
    };
  },
);

type TShippingAddressSummary = Pick<
  IEffectiveCreateSendSettings,
  'shippingAddressSummary' | 'shippingFixedAddress' | 'shippingAddressCount'
>;

const NO_SHIPPING_ADDRESS_SUMMARY: TShippingAddressSummary = {
  shippingAddressSummary: null,
  shippingFixedAddress: null,
  shippingAddressCount: 0,
};

const resolveShippingAddressSummary = (
  mode: TEffectiveShippingMode | null,
  overrideAddress: IOrgAddress | null,
  claimAddressIds: string[],
): TShippingAddressSummary => {
  if (mode === ShippingModeEnum.YouProvide || mode === ShippingModeEnum.FixedAddress) {
    return overrideAddress
      ? { shippingAddressSummary: 'fixed', shippingFixedAddress: overrideAddress, shippingAddressCount: 0 }
      : NO_SHIPPING_ADDRESS_SUMMARY;
  }

  if (mode === ShippingModeEnum.RecipientDecides || mode === ShippingModeEnum.FromList) {
    return claimAddressIds.length
      ? {
          shippingAddressSummary: mode === ShippingModeEnum.FromList ? 'list' : 'suggested',
          shippingFixedAddress: null,
          shippingAddressCount: claimAddressIds.length,
        }
      : NO_SHIPPING_ADDRESS_SUMMARY;
  }

  return NO_SHIPPING_ADDRESS_SUMMARY;
};

export const selectEffectiveCsSendSettings = createSelector(
  selectCreateSendMethod,
  selectIsCreateSendContentDigital,
  selectIsLinkPayloadDigital,
  selectEffectiveShipping,
  selectCsOnHoldEnabled,
  selectCsOnHoldUntil,
  selectCsClaimExpiration,
  selectCsSobEnabled,
  selectCsIsSobActive,
  selectCsSobEmail,
  selectCsMultiKitAddressMode,
  selectCsOverrideAddress,
  selectCsClaimAddressIds,
  selectCsBusinessReasonId,
  selectCsIsOfficeDropshipEnabled,
  selectCsIsGroupShipmentApplicable,
  selectCsDisableRecipientShippedEmail,
  selectCsDisableSenderEmails,
  (
    method,
    isContentDigital,
    isLinkPayloadDigital,
    effective,
    onHoldEnabled,
    onHoldUntil,
    claimExpiration,
    sobEnabled,
    isSobActive,
    sobEmail,
    linkAddressMode,
    overrideAddress,
    claimAddressIds,
    businessReasonId,
    isOfficeDropshipEnabled,
    isGroupShipmentApplicable,
    isRecipientShippedEmailDisabled,
    isSenderNotificationDisabled,
  ): IEffectiveCreateSendSettings => {
    const isLink = method === CreateSendMethodEnum.Link;
    const isRecipients = method === CreateSendMethodEnum.Recipients;

    const isOnHoldApplicable = !isLink && !isContentDigital;
    const isLinkShippingApplicable = isLink && !isLinkPayloadDigital;
    const isRecipientsShippingApplicable = isRecipients && !isContentDigital;
    const isClaimExpiryApplicable = isRecipients && effective.isClaimBased;

    const isOnHold = isOnHoldApplicable && onHoldEnabled;
    const isOnBehalf = isSobActive && sobEnabled && (isLink || isRecipients);

    const shippingMode: TEffectiveShippingMode | null = isLinkShippingApplicable
      ? linkAddressMode
      : isRecipientsShippingApplicable
      ? effective.mode
      : null;

    return {
      isOnHold,
      onHoldUntil: isOnHold ? onHoldUntil : '',
      claimExpiration: isClaimExpiryApplicable ? claimExpiration : '',
      isOnBehalf,
      sobEmail: isOnBehalf ? sobEmail : null,
      ...resolveShippingAddressSummary(shippingMode, overrideAddress, claimAddressIds),
      isGroupShipment:
        (isLinkShippingApplicable || isRecipientsShippingApplicable) &&
        isGroupShipmentApplicable &&
        isOfficeDropshipEnabled,
      businessReasonId,
      isRecipientShippedEmailDisabled: !isContentDigital && isRecipientShippedEmailDisabled,
      isRecipientTrackingApplicable: !isContentDigital,
      isSenderNotificationDisabled,
    };
  },
);

export const selectEffectiveCsInternalRecords = createSelector(
  selectCsBusinessReasonId,
  selectCsInternalNotes,
  (businessReasonId, internalNotes): IEffectiveCreateSendInternalRecords => ({
    isOn: true,
    businessReasonId,
    internalNote: internalNotes,
  }),
);

export const selectEffectiveCsRecipients = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCompleteRecipients,
  (visibleSteps, recipients): IEffectiveCreateSendRecipients => {
    const isOn = visibleSteps.has('recipients');

    return { isOn, recipients: isOn ? recipients : [] };
  },
);

export const selectEffectiveCsShipmentInfo = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCsShipmentInfoForm,
  (visibleSteps, form): IEffectiveCreateSendShipmentInfo => {
    const isOn = visibleSteps.has('shipment-info');

    return { isOn, form: isOn ? form : DEFAULT_CREATE_SEND_SHIPMENT_INFO_STATE.form };
  },
);

export const selectEffectiveCsClaimPage = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCsClaimPageExperienceId,
  (visibleSteps, experienceId): IEffectiveCreateSendClaimPage => {
    const isOn = visibleSteps.has('claim-page');

    return { isOn, experienceId: isOn ? experienceId ?? '' : '' };
  },
);

export const selectEffectiveCsLinkSettings = createSelector(
  selectCreateSendVisibleStepSlugs,
  selectCsLinkTitle,
  selectCsLinkExpirationDate,
  selectIsLinkOnHoldApplicable,
  selectCsLinkOnHoldUntil,
  selectCsRedemptionLimit,
  selectCsLinkPassword,
  selectCsAllowedDomains,
  selectCsIs2FaEnabled,
  (
    visibleSteps,
    title,
    expirationDate,
    isOnHoldApplicable,
    onHoldUntil,
    redemptionLimit,
    password,
    allowedDomains,
    is2FaEnabled,
  ): IEffectiveCreateSendLinkSettings => {
    const isOn = visibleSteps.has('link-settings');

    return {
      isOn,
      title: isOn ? title ?? '' : '',
      expirationDate: isOn ? expirationDate : '',
      onHoldUntil: isOn && isOnHoldApplicable ? onHoldUntil : '',
      redemptionLimit: isOn ? redemptionLimit : null,
      password: isOn ? password : '',
      allowedDomains: isOn ? allowedDomains ?? [] : [],
      is2FaEnabled: isOn && Boolean(is2FaEnabled),
    };
  },
);
